import { CSSProperties } from 'react';

import { WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

/**
 * The colour layer of the page background: the configured background colour, or the gradient
 * between it and `backgroundColorGradient`.
 *
 * The value is only meaningful on a viewport-sized element — that is how the layouts paint it
 * (see `PageBackgroundColorLayer`), so the gradient maps onto the viewport rather than onto the
 * element. Anything reproducing the page background has to keep that geometry to get the same
 * pixels.
 *
 * Always a `background-image`, never a `background-color`, even for a single colour. Safari 26
 * ignores `<meta name="theme-color">` and instead derives its top bar from the `position: fixed`
 * and `sticky` boxes at the viewport edges: given a visible `background-color` on one, it replaces
 * the translucent material with an opaque block of that colour, hides the scroll-edge fade, and
 * keeps that colour for the rest of the page visit. Declaring none keeps the bar translucent for
 * this layer, verified on iOS 26.
 *
 * Do not read that as "background images are invisible to Safari" — they are not. Where no
 * `background-color` is found, WebKit falls back to sampling the pixels of the fixed and sticky
 * layers, and a small sticky banner painting a flat colour that way does still tint the bar (which
 * is why the app-download banner turns it black). A viewport-sized layer like this one is the case
 * that stays translucent. The colour the browser UI should tint itself with comes from the document
 * canvas instead — see the `html, body` rule in `globals.css`, which cannot form such an edge.
 */
export function getPageBackgroundStyle(style: WebsiteStyle | undefined): CSSProperties {
    const from = style?.backgroundColor;
    if (!from) {
        return {};
    }
    const to = style?.backgroundColorGradient || from;
    return {
        backgroundImage: `linear-gradient(var(--gradient-angle, 135deg), ${from}, ${to})`
    };
}
